import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import { ModalsProvider } from '@mantine/modals';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import { handleError } from '@services/errHandler';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridProvider } from 'ag-grid-react';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { initVendorLicenses } from 'src/config/licenses';
import 'survey-analytics/survey.analytics.css';
import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import { bootstrapApp } from './bootstrap/startup';
import { initSentry } from './config/sentry';
import { cssVariablesResolver, getThemeConfig } from './config/theme';
import { AppNotifications } from './core-components/Notifications';
import { NetworkStatusBootstrap } from './hooks/NetworkStatusBootstrap';
import './i18n';
import './index.css';
import { AppColorSchemeProvider } from './providers/color-scheme/AppColorSchemeProvider';
import { themeContext } from './stores/utility-stores/appTheme';

if (import.meta.env.PROD) {
  registerSW({ immediate: true });
}

// After a new deploy, old tabs can still reference chunk files that no longer
// exist; the hosting rewrite then serves index.html (text/html) for them,
// which Vite reports as a preload error. Reload once to pick up the new build.
window.addEventListener('vite:preloadError', () => {
  const reloadKey = 'vite-preload-error-reload';
  if (sessionStorage.getItem(reloadKey)) return;
  sessionStorage.setItem(reloadKey, '1');
  window.location.reload();
});

ModuleRegistry.registerModules([AllCommunityModule]);
initVendorLicenses().catch((error) => {
  handleError('Failed to initialize vendor licenses:', error);
});
initSentry();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
const root = rootElement;
const appRoot = ReactDOM.createRoot(root);

function renderWithTheme() {
  const themeColor = themeContext.getState().selectedThemeColor;
  const theme = getThemeConfig(themeColor);

  appRoot.render(
    <StrictMode>
      <AgGridProvider modules={[AllCommunityModule]}>
        <MantineProvider
          defaultColorScheme="light"
          theme={theme}
          cssVariablesResolver={cssVariablesResolver}
        >
          <AppColorSchemeProvider>
            <ModalsProvider>
              <NetworkStatusBootstrap />
              <AppNotifications />
              <App />
            </ModalsProvider>
          </AppColorSchemeProvider>
        </MantineProvider>
      </AgGridProvider>
    </StrictMode>
  );
}

async function renderApp() {
  await bootstrapApp();
  renderWithTheme();
  // Successful render means the current build is healthy; allow future preload
  // errors (e.g. after the next deploy) to trigger a reload again.
  sessionStorage.removeItem('vite-preload-error-reload');

  themeContext.subscribe((state, prevState) => {
    if (state.selectedThemeColor !== prevState.selectedThemeColor) {
      renderWithTheme();
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-floating-promises
void renderApp();
